'use client';

import useEmblaCarousel from 'embla-carousel-react';
import { Children, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { CarouselDotNav } from '../DotNav/DotNav';
import { CarouselThumbNav } from '../ThumbNav/ThumbNav';
import type { CarouselType } from '../types';
import { cn } from '../../../utils/cn/cn';
import { subscribeToNothing } from '../../../utils/subscribeToNothing/subscribeToNothing';
import { getEmblaPlugins } from './helper';
import { ArrowButton } from '../ArrowButton/ArrowButton';

/**
 * Carousel Component based on https://www.embla-carousel.com
 * A11y-Pattern: https://www.w3.org/WAI/ARIA/apg/patterns/carousel/examples/carousel-1-prev-next/
 * @deprecated please use the Carousel from packages/modules
 */
export const Carousel = ({
  className = '',
  children,
  showDots = false,
  showArrows = true,
  arrowsInsideBoundary = false,
  visibleSlides = 1,
  slidesToScroll = 1,
  gap = 0,
  autoplay = false,
  loop = false,
  emblaOptions = {},
  thumbnailConfig,
  loadingState = false,
  index: indexToSelect = 0,
  onChange = () => {},
  aria,
  hasOffset = false,
}: CarouselType) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(indexToSelect);
  const prefersReducedMotion = useSyncExternalStore(
    subscribeToNothing,
    () => Boolean(autoplay) && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    () => false,
  );

  const userHasClickedIsPlaying = useRef(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Main Carousel Settings
  const [viewportRef, embla] = useEmblaCarousel(
    {
      duration: prefersReducedMotion ? 0 : 32,
      loop,
      align: 'start',
      slidesToScroll,
      containScroll: 'trimSnaps',
      ...emblaOptions,
    },
    getEmblaPlugins(autoplay),
  );

  const autoplayPlugin = embla?.plugins()?.autoplay;
  const subscribeToAutoplay = (onStoreChange: () => void) => {
    if (!embla) return () => {};
    // Embla emits autoplay:play and autoplay:stop before isPlaying() changes, so React reads it later.
    const notify = () => queueMicrotask(onStoreChange);
    embla.on('autoplay:play', notify).on('autoplay:stop', notify).on('reInit', notify);
    return () => {
      embla.off('autoplay:play', notify).off('autoplay:stop', notify).off('reInit', notify);
    };
  };
  const isPlaying = useSyncExternalStore(
    subscribeToAutoplay,
    () => autoplayPlugin?.isPlaying() ?? Boolean(autoplay),
    () => Boolean(autoplay),
  );

  const setAutoplay = (option: 'start' | 'stop') => {
    if (!autoplayPlugin) return;
    if (option === 'start') {
      autoplayPlugin.play();
    } else {
      autoplayPlugin.stop();
    }
  };
  // init everything
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (embla) {
      const onSelect = () => {
        setSelectedIndex(embla.selectedScrollSnap());
        onChange(embla.selectedScrollSnap());
      };

      onSelect();

      embla.on('select', onSelect);

      return () => {
        embla.off('select', onSelect);
      };
    }
  }, [embla, onChange]);

  useEffect(() => {
    if (embla && embla.selectedScrollSnap() !== indexToSelect) {
      embla.scrollTo(indexToSelect, true);
    }
  }, [indexToSelect, embla]);

  /**
   * check if user has reduced motion active. If yes, stop autoplay.
   */
  useEffect(() => {
    if (prefersReducedMotion) {
      autoplayPlugin?.stop();
    }
  }, [autoplayPlugin, prefersReducedMotion]);

  const renderArrows = embla !== undefined && (showArrows === true || showArrows === 'hover');

  const scrollSnaps = embla?.scrollSnapList();

  const noThumbnails =
    thumbnailConfig === undefined || (thumbnailConfig && thumbnailConfig.thumbnails.length === 0);

  const onMouseEnter = () => {
    if (showArrows === 'hover') {
      wrapperRef.current?.classList.add('show-arrows');
    }
    if (!userHasClickedIsPlaying.current) {
      // if the user enters with the mouse, stop the rotation unless they already set the rotation state manually
      setAutoplay('stop');
      userHasClickedIsPlaying.current = true;
    }
  };
  const onMouseLeave = () => {
    if (showArrows === 'hover') {
      wrapperRef.current?.classList.remove('show-arrows');
    }
  };

  return (
    <section
      data-carousel
      aria-roledescription={aria?.galleryRoleDescription ?? 'Slideshow'}
      aria-label={aria?.galleryAriaLabel}
      className="flex-1 flex-col gap-1.5"
    >
      <div
        className={cn(`group relative ${showArrows === true ? 'show-arrows' : ''}`, className)}
        ref={wrapperRef}
        onMouseEnter={() => onMouseEnter()}
        onMouseLeave={() => onMouseLeave()}
        // TODO: make buttons accessible for when showArrows="hover" is set (or remove the "hover" option)
      >
        <div
          className={`h-full w-full overflow-x-clip ${hasOffset ? 'p-1/4' : ''}`}
          data-index={selectedIndex}
        >
          {/* buttons need to be the first elements to be focussable, so they need to be in the DOM before the slides */}
          {autoplay && (
            <ArrowButton
              className="top-auto bottom-[-0.5rem] left-[calc(50%-1rem)] z-[1] p-0"
              onClick={() => setAutoplay(isPlaying ? 'stop' : 'start')}
              {...(isPlaying &&
                aria?.buttonAutoplayPause && {
                  'aria-label': aria.buttonAutoplayPause,
                })}
              {...(!isPlaying &&
                aria?.buttonAutoplayStart && {
                  'aria-label': aria.buttonAutoplayStart,
                })}
            >
              <span className={`${isPlaying ? 'icon-emp-pause' : 'icon-emp-play'} h-2.5 w-2.5`} />
            </ArrowButton>
          )}
          {renderArrows && (
            <>
              {embla.canScrollPrev() && (
                <ArrowButton
                  className={`${arrowsInsideBoundary ? 'left-1.5' : '-left-2.5'} hidden group-[.show-arrows]:flex ${showArrows === true ? 'flex' : ''} z-[1] p-0`}
                  onClick={() => {
                    setAutoplay('stop');
                    embla.scrollPrev();
                  }}
                  {...(aria?.buttonPrev && {
                    'aria-label': aria.buttonPrev,
                  })}
                >
                  <span className="icon-emp-arrow-medium-left h-4 w-4" />
                </ArrowButton>
              )}

              {embla.canScrollNext() && (
                <ArrowButton
                  className={`${arrowsInsideBoundary ? 'right-1.5' : '-right-2.5'} hidden group-[.show-arrows]:flex ${showArrows === true ? 'flex' : ''} z-[1] p-0`}
                  onClick={() => {
                    setAutoplay('stop');
                    embla.scrollNext();
                  }}
                  {...(aria?.buttonNext && {
                    'aria-label': aria.buttonNext,
                  })}
                >
                  <span className="icon-emp-arrow-medium-right h-4 w-4" />
                </ArrowButton>
              )}
            </>
          )}

          <div className="h-full" ref={loadingState ? null : viewportRef}>
            <div
              className="flex h-full"
              style={{ gap: typeof gap === 'number' ? `${gap * 0.5}rem` : gap }}
              aria-live={isPlaying ? 'off' : 'polite'}
            >
              {Children.map(children, (child, index) => (
                <div
                  aria-label={
                    aria?.slideAriaLabel?.(index, Children.count(children)) ?? `${index + 1}`
                  }
                  aria-roledescription={aria?.slideRoleDescription ?? 'Slide'}
                  role="group"
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  key={index}
                  // grid stretches the slide content to the height of the tallest slide
                  className="relative min-w-0 *:grid *:h-full *:w-full"
                  style={{
                    flex: `0 0 ${
                      typeof visibleSlides === 'number'
                        ? `calc((100% / ${visibleSlides}) - (((${visibleSlides} - 1) / ${visibleSlides}) * ${
                            typeof gap === 'number' ? `${gap * 0.5}rem` : gap
                          }))`
                        : visibleSlides
                    }`,
                  }}
                >
                  <div
                    inert={selectedIndex !== index} // prevent inactive content from being focussable
                    aria-hidden={selectedIndex !== index} // hide inactive content from accessibility tree
                  >
                    {child}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {showDots && scrollSnaps && scrollSnaps.length > 1 && (
        <CarouselDotNav
          scrollSnaps={scrollSnaps}
          selectedIndex={selectedIndex}
          scrollTo={(index: number) => embla?.scrollTo(index)}
        />
      )}

      {/* eslint-disable-next-line no-nested-ternary */}
      {noThumbnails ? null : !embla ? (
        // fallback just needed to fix layout shift
        <div className="h-[60px] lg:h-[96px]" />
      ) : (
        <CarouselThumbNav
          emblaMain={embla}
          thumbnailConfig={thumbnailConfig}
          selectedIndex={selectedIndex}
        />
      )}
    </section>
  );
};
